import { Phone, Mail, MapPin, Clock } from "lucide-react";
import Seo from "@/components/Seo";
import SectionHeading from "@/components/SectionHeading";
import QuoteForm from "@/components/QuoteForm";
import { business } from "@/data/site";

const schema = {
  "@context": "https://schema.org",
  "@type": "RoofingContractor",
  name: business.name,
  telephone: "+1-215-806-5038",
  email: business.email,
  address: {
    "@type": "PostalAddress",
    streetAddress: business.address.street,
    addressLocality: business.address.city,
    addressRegion: business.address.state,
    postalCode: business.address.zip,
    addressCountry: "US",
  },
  areaServed: "Bucks County, Pennsylvania",
};

const details = [
  { icon: Phone, label: "Phone", value: business.phone, href: business.phoneHref, testid: "contact-phone" },
  { icon: Mail, label: "Email", value: business.email, href: business.emailHref, testid: "contact-email" },
  { icon: MapPin, label: "Address", value: business.address.full },
  { icon: Clock, label: "Hours", value: business.hours },
  { icon: MapPin, label: "Service Area", value: business.serviceArea },
];

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact & Free Estimate | Future Roofing and Repair — Bucks County, PA"
        description="Contact Future Roofing and Repair in Bristol, PA. Call 215-806-5038 or request a free estimate online. Residential & commercial roofing, repairs, gutters, siding and roof maintenance."
        keywords="Roofer Bristol PA, Roofing company Bucks County PA contact, Free roofing estimate, Roof repair Bucks County"
        schema={schema}
      />

      {/* Header band */}
      <section className="bg-charcoal relative grain border-b border-charcoal/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 relative">
          <p className="font-accent uppercase tracking-[0.25em] text-sm text-gold mb-3">Contact Us</p>
          <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl uppercase text-cream leading-[0.9] max-w-3xl">
            Get In Touch & Request an Estimate
          </h1>
          <p className="mt-5 text-cream/85 max-w-2xl leading-relaxed">
            Call us for the fastest response, or fill out the form and we'll get back to you quickly.
          </p>
          <a
            href={business.phoneHref}
            data-testid="contact-hero-call"
            className="mt-7 inline-flex items-center gap-2 bg-rust text-cream border border-cream/40 shadow-md hover:-translate-y-1 transition-all font-heading text-2xl uppercase px-7 py-4"
          >
            <Phone className="w-6 h-6" /> Call {business.phone}
          </a>
        </div>
      </section>

      <section className="bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Details + map */}
          <div>
            <SectionHeading kicker="Reach Us" title="Future Roofing and Repair" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {details.map((d) => {
                const content = (
                  <>
                    <d.icon className="w-6 h-6 text-rust mb-2" />
                    <p className="font-accent uppercase text-xs tracking-widest text-charcoal/60">
                      {d.label}
                    </p>
                    <p className="font-semibold text-charcoal break-words">{d.value}</p>
                  </>
                );
                return d.href ? (
                  <a
                    key={d.label}
                    href={d.href}
                    data-testid={d.testid}
                    className="bg-cream border border-charcoal/20 shadow-md p-5 hover:-translate-y-0.5 transition-transform"
                  >
                    {content}
                  </a>
                ) : (
                  <div
                    key={d.label}
                    className="bg-cream border border-charcoal/20 shadow-md p-5"
                  >
                    {content}
                  </div>
                );
              })}
            </div>

            <div className="mt-8 border border-charcoal/20 shadow-md">
              <iframe
                title="Future Roofing and Repair location"
                src={business.mapEmbed}
                width="100%"
                height="320"
                style={{ border: 0, display: "block" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </div>

          {/* Form */}
          <div>
            <SectionHeading kicker="Free Estimate" title="Request an Estimate" />
            <QuoteForm />
          </div>
        </div>
      </section>
    </>
  );
}
